import { AlertTriangle, CheckCircle2, Clock, RefreshCw, XCircle } from 'lucide-react';

import { Badge, IconButton, cn } from '@/components/ui';

type ExprReasonNode = {
  type: string;
  value?: number;
  missing?: boolean;
  error?: string;
  ref?: string;
  left?: ExprReasonNode;
  right?: ExprReasonNode;
};

type HysteresisReason = {
  mode?: 'activation' | 'release';
  activation?: ExprReasonNode;
  release?: ExprReasonNode;
};

type ReasonNode = {
  type: string;
  children?: ReasonNode[];
  child?: ReasonNode;
  op?: string;
  left?: ExprReasonNode;
  right?: ExprReasonNode;
  hysteresis?: HysteresisReason;
  start?: string;
  end?: string;
  current?: string;
  result: boolean;
  error?: string;
  missing?: boolean;
};

type ConditionEvaluation = {
  result: boolean;
  reason?: ReasonNode | null;
};

export type SceneConditionEvaluation = {
  trigger?: ConditionEvaluation | null;
  preconditions?: ConditionEvaluation | null;
  triggerConfigured: boolean;
  preconditionsConfigured: boolean;
  triggerOk: boolean;
  preconditionsOk: boolean;
  effectiveOk: boolean;
  manualOverride?: 'on' | 'off' | null;
  evaluatedAt: string;
};

interface SceneConditionEvaluationProps {
  evaluation?: SceneConditionEvaluation | null;
  isActive: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const compareOps: Record<string, string> = {
  eq: '=',
  ne: '!=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
};

const exprOps: Record<string, string> = {
  add: '+',
  sub: '-',
  mul: '*',
  div: '/',
};

const offCompareOps: Record<string, string> = {
  gt: '<=',
  gte: '<',
  lt: '>=',
  lte: '>',
};

function formatNumber(value?: number): string {
  if (value === undefined || Number.isNaN(value)) {
    return 'n/a';
  }
  return Number.isInteger(value) ? value.toString() : value.toFixed(3);
}

function formatExpr(expr?: ExprReasonNode): string {
  if (!expr) {
    return 'unknown';
  }

  if (expr.missing) {
    return expr.ref ? `${expr.ref} (missing)` : 'missing';
  }

  if (expr.error) {
    return expr.ref ? `${expr.ref} (error)` : 'error';
  }

  if (expr.type === 'literal') {
    return formatNumber(expr.value);
  }

  if (expr.type === 'sensor') {
    const value = formatNumber(expr.value);
    return expr.ref ? `${expr.ref} = ${value}` : value;
  }

  if (exprOps[expr.type]) {
    const left = formatExpr(expr.left);
    const right = formatExpr(expr.right);
    const op = exprOps[expr.type];
    const value = formatNumber(expr.value);
    return `(${left} ${op} ${right}) = ${value}`;
  }

  return expr.type;
}

function formatHysteresisSummary(node: ReasonNode): string | null {
  if (node.type !== 'compare' || !node.hysteresis?.activation || !node.hysteresis?.release) {
    return null;
  }

  const op = compareOps[node.op ?? ''] ?? node.op ?? '?';
  const offOp = offCompareOps[node.op ?? ''] ?? '?';
  const activation = formatExpr(node.hysteresis.activation);
  const release = formatExpr(node.hysteresis.release);

  return `On at ${op} ${activation} / Off at ${offOp} ${release}`;
}

function formatReasonLabel(node: ReasonNode): string {
  switch (node.type) {
    case 'all':
      return 'ALL conditions';
    case 'any':
      return 'ANY condition';
    case 'not':
      return 'NOT';
    case 'compare': {
      const left = formatExpr(node.left);
      const right = formatExpr(node.right);
      const op = compareOps[node.op ?? ''] ?? node.op ?? '?';
      return `${left} ${op} ${right}`;
    }
    case 'timeBetween':
      return `Time between ${node.start ?? '?'} and ${node.end ?? '?'} (now ${
        node.current ?? '?'
      })`;
    default:
      return node.type;
  }
}

function ResultBadge({
  result,
  missing,
  error,
}: {
  result: boolean;
  missing?: boolean;
  error?: string;
}) {
  if (missing) {
    return (
      <Badge variant="warning" size="sm" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        Missing
      </Badge>
    );
  }

  if (error) {
    return (
      <Badge variant="warning" size="sm" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        Error
      </Badge>
    );
  }

  if (result) {
    return (
      <Badge variant="success" size="sm" className="gap-1">
        <CheckCircle2 className="h-3 w-3" />
        True
      </Badge>
    );
  }

  return (
    <Badge variant="error" size="sm" className="gap-1">
      <XCircle className="h-3 w-3" />
      False
    </Badge>
  );
}

function ReasonNodeView({ node, depth }: { node: ReasonNode; depth: number }) {
  const hasChildren = Boolean(node.children?.length || node.child);
  const children = node.children ?? (node.child ? [node.child] : []);
  const hysteresisSummary = formatHysteresisSummary(node);

  return (
    <div
      className={cn(
        'relative flex flex-col gap-2',
        depth > 0 && 'border-l border-dashed border-gray-200 pl-4 dark:border-white/[0.06]'
      )}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-zinc-200">
        <span className="font-medium">{formatReasonLabel(node)}</span>
        <ResultBadge result={node.result} missing={node.missing} error={node.error} />
      </div>

      {hysteresisSummary && (
        <div className="text-xs text-gray-500 dark:text-zinc-500">{hysteresisSummary}</div>
      )}

      {node.error && <div className="text-xs text-amber-600 dark:text-amber-400">{node.error}</div>}

      {hasChildren && (
        <div className="flex flex-col gap-3">
          {children.map((child, index) => (
            <ReasonNodeView key={`${child.type}-${index}`} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function ConditionSection({
  label,
  configured,
  evaluation,
}: {
  label: string;
  configured: boolean;
  evaluation?: ConditionEvaluation | null;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 bg-gray-50/70 p-4',
        'dark:border-white/[0.06] dark:bg-white/[0.02]'
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-900 dark:text-white">{label}</div>
        {configured ? (
          evaluation ? (
            <ResultBadge result={evaluation.result} />
          ) : (
            <Badge variant="info" size="sm">
              Pending
            </Badge>
          )
        ) : (
          <Badge variant="default" size="sm">
            Not configured
          </Badge>
        )}
      </div>

      {configured ? (
        evaluation?.reason ? (
          <ReasonNodeView node={evaluation.reason} depth={0} />
        ) : (
          <div className="text-xs text-gray-500 dark:text-zinc-500">
            Waiting for evaluation data.
          </div>
        )
      ) : (
        <div className="text-xs text-gray-500 dark:text-zinc-500">
          Add conditions to enable automation for this section.
        </div>
      )}
    </div>
  );
}

function formatTimeLabel(timestamp?: string): string {
  if (!timestamp) {
    return 'Unknown time';
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

export function SceneConditionEvaluation({
  evaluation,
  isActive,
  onRefresh,
  isRefreshing,
}: SceneConditionEvaluationProps) {
  const manualOnOverride =
    Boolean(evaluation) &&
    evaluation?.manualOverride === 'on' &&
    isActive &&
    !evaluation?.effectiveOk;
  const manualOffOverride =
    Boolean(evaluation) &&
    evaluation?.manualOverride === 'off' &&
    !isActive &&
    evaluation?.effectiveOk;

  return (
    <div
      className={cn(
        'rounded-xl border border-gray-200 bg-white p-5',
        'dark:border-white/[0.06] dark:bg-[#18181b]'
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-medium text-gray-900 dark:text-white">
            Condition Evaluation
          </h2>
          <p className="text-xs text-gray-500 dark:text-zinc-500">
            Evaluation snapshot based on current sensor values.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {evaluation && (
            <Badge variant={evaluation.effectiveOk ? 'success' : 'error'} size="sm">
              {evaluation.effectiveOk ? 'Conditions met' : 'Conditions not met'}
            </Badge>
          )}
          {onRefresh && (
            <IconButton
              icon={<RefreshCw />}
              label="Refresh evaluation"
              variant="secondary"
              size="sm"
              loading={isRefreshing}
              onClick={onRefresh}
            />
          )}
        </div>
      </div>

      {manualOnOverride && (
        <div
          className={cn(
            'mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800',
            'dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200'
          )}
        >
          Scene is active due to a manual trigger, but conditions are currently false.
        </div>
      )}
      {manualOffOverride && (
        <div
          className={cn(
            'mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800',
            'dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200'
          )}
        >
          Scene was manually turned off while conditions are true. It will stay off until conditions
          go false and then true (or you activate it manually).
        </div>
      )}

      {!evaluation && (
        <div className="mt-4 text-sm text-gray-500 dark:text-zinc-500">
          Condition evaluation data is not available yet.
        </div>
      )}

      {evaluation && (
        <>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <ConditionSection
              label="Trigger"
              configured={evaluation.triggerConfigured}
              evaluation={evaluation.trigger}
            />
            <ConditionSection
              label="Preconditions"
              configured={evaluation.preconditionsConfigured}
              evaluation={evaluation.preconditions}
            />
          </div>

          <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
            <Clock className="h-3.5 w-3.5" />
            Evaluated at {formatTimeLabel(evaluation.evaluatedAt)}
          </div>
        </>
      )}
    </div>
  );
}
